import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import "../styles/About.css";
import { useNavigate } from "react-router-dom";   // ✅ added

// Images
import car1 from "../assets/tt4.jpg";
import car2 from "../assets/tt28.jpeg";
import car3 from "../assets/tt21.png";
import car4 from "../assets/tt18.png";
import car5 from "../assets/tt2.jpeg";

import vanLogo from "../assets/van-logo.png";

// ===== FADE UP VARIANT =====
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

// ===== ANIMATED TITLE =====
const AnimatedTitle = ({ text }) => {
  const titleRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.3 }
    );

    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <h2 className="about-section-subtitle animated-title" ref={titleRef}>
      {text.split(" ").map((word, wordIndex) => (
        <span key={wordIndex} className="animated-word">
          {word.split("").map((letter, letterIndex) => (
            <span
              key={letterIndex}
              className={`animated-letter ${visible ? "show" : ""}`}
              style={{
                transitionDelay: `${wordIndex * 300 + letterIndex * 40}ms`,
              }}
            >
              {letter}
            </span>
          ))}
          {" "}
        </span>
      ))}
    </h2>
  );
};

// ===== Animated Circle =====
const AnimatedCircle = ({ percent, label }) => {
  const pathRef = useRef(null);
  const textRef = useRef(null);
  const containerRef = useRef(null);
  const [startAnimation, setStartAnimation] = useState(false);

  const RADIUS = 40;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && setStartAnimation(true),
      { threshold: 0.4 }
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // ✅ FIXED ANIMATION (NULL-SAFE)
  useEffect(() => {
    if (!startAnimation) return;

    const duration = 3000;
    const startTime = performance.now();

    const animate = (time) => {
      const progress = Math.min((time - startTime) / duration, 1);
      const value = Math.ceil(progress * percent);

      if (textRef.current) {
        textRef.current.textContent = value + "%";
      }

      const dash = (CIRCUMFERENCE * value) / 100;

      if (pathRef.current) {
        pathRef.current.setAttribute(
          "stroke-dasharray",
          `${dash}, ${CIRCUMFERENCE}`
        );
      }

      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [startAnimation, percent]);

  return (
    <div className="about-section-stat-box" ref={containerRef}>
      <svg viewBox="0 0 100 100" width="100" height="100">
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="#fcb016"
          strokeWidth="5"
          opacity="0.3"
        />

        <path
          ref={pathRef}
          fill="none"
          strokeLinecap="round"
          strokeWidth="5"
          stroke="#fcb016"
          strokeDasharray="0, 251.2"
          d="M50 10
             a 40 40 0 0 1 0 80
             a 40 40 0 0 1 0 -80"
        />

        <text
          ref={textRef}
          x="50"
          y="50"
          textAnchor="middle"
          dy="7"
          fontSize="20"
          fontWeight="700"
          fill="#fcb016"
        >
          0%
        </text>
      </svg>

      <p>{label}</p>
    </div>
  );
};

// ===== MAIN ABOUT COMPONENT =====
const About = () => {
  const images = [car1, car2, car3, car4, car5];
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleImageClick = () => {
    setCurrent((prev) => (prev + 1) % images.length);
  };

  const navigate = useNavigate();   // ✅ added

  // ===== POSITION AWARE BUTTON LOGIC =====
  const handleMove = (e) => {
    const btn = e.currentTarget;
    const circle = btn.querySelector(".hover-circle");

    const rect = btn.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    circle.style.left = x + "px";
    circle.style.top = y + "px";
  };

  return (
    <section className="about-section" id="about">
      <motion.div
        className="about-section-container"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        {/* LEFT CONTENT */}
        <motion.div className="about-section-left" variants={fadeUp}>
          <motion.div className="about-section-tag" variants={fadeUp}>
            <img src={vanLogo} alt="van icon" />
            <span>ABOUT</span>
          </motion.div>

          <motion.div variants={fadeUp}>
            <AnimatedTitle text="WELCOME TO MAHA CABS" />
          </motion.div>

          <motion.p className="about-section-desc" variants={fadeUp}>
            Maha Cabs offers the best van rental services with a focus on
            safety and comfort. Our professional drivers are available 24/7
            providing reliable and hassle-free travel across India.
          </motion.p>

          <motion.div
            className="about-section-list"
            variants={fadeUp}
            style={{ display: "flex", gap: "50px" }}
          >
            <ul style={{ listStyle: "disc" }}>
              <li>24*7 Available</li>
              <li>18 Seater</li>
              <li>All India Trips</li>
              <li>Safe & Secure</li>
              <li>Experienced Driver</li>
            </ul>

            <ul style={{ listStyle: "disc" }}>
              <li>Luxury Pushback Seats</li>
              <li>AC or Non-AC</li>
              <li>LED TV With Uber Speaker</li>
              <li>Free Wifi</li>
              <li>Music System with LED</li>
            </ul>
          </motion.div>

          <motion.div className="about-section-stats-row" variants={fadeUp}>
            <AnimatedCircle percent={98} label="Satisfied Clients" />
            <AnimatedCircle percent={100} label="Success Rate" />
          </motion.div>

          {/* ===== UPDATED BUTTON ===== */}
          <motion.div className="about-section-cta" variants={fadeUp}>
            <button
              className="about-section-read-more btn-6"
              onClick={() => navigate("/gallery")}   // ✅ added
              onMouseEnter={handleMove}
              onMouseLeave={handleMove}
            >
              <span className="hover-circle"></span>
              See More Photos →
            </button>
          </motion.div>
        </motion.div>

        {/* RIGHT SIDE IMAGES */}
        <motion.div className="about-section-right" variants={fadeUp}>
          <div
            className="about-slider-wrapper"
            onClick={handleImageClick}
            style={{ cursor: "pointer" }}
          >
            <div className="about-slides">
              {images.map((img, index) => {
                let className = "about-slide";

                if (index === current) className += " current";
                else if (index === (current + 1) % images.length)
                  className += " next";
                else if (
                  index ===
                  (current - 1 + images.length) % images.length
                )
                  className += " prev";

                return (
                  <div key={index} className={className}>
                    <img src={img} alt={`slide-${index}`} />
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default About;
